import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '../../lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  asChild?: boolean;
  loading?: boolean;
}

const variants: Record<Variant, string> = {
  primary: 'bg-primary text-primary-fg hover:bg-primary-hover disabled:bg-ink-subtle',
  secondary: 'bg-surface text-ink border border-line-strong hover:bg-sunken disabled:text-ink-subtle',
  ghost: 'bg-transparent text-ink-muted hover:bg-sunken hover:text-ink',
  danger: 'bg-alert text-white hover:opacity-90 disabled:opacity-50',
};

const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-9 px-4 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'primary', size = 'md', asChild, loading, disabled, children, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  // Radix `Slot` requires exactly one React element child — it clones its
  // props onto that one child rather than rendering a wrapper of its own.
  // `{loading && <Spinner />}` beside `children` is a second (if falsy)
  // child in the array, which `Slot` rejects ("Expected a single React
  // element child") even though plain DOM rendering tolerates a stray
  // `false`. Found rendering `ResetPasswordPage`'s `<Button asChild>` for
  // real in a browser for the first time (Stage 4 Phase 1) — every render
  // of `asChild` before this went untested. `asChild` usages in this app
  // are all navigational (a styled `<Link>`), never `loading`, so this
  // just skips the spinner slot entirely in that mode rather than trying
  // to keep it working through a child `<Slottable>` wrapper.
  return (
    <Comp
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-tight font-medium transition-colors',
        'disabled:cursor-not-allowed',
        variants[variant],
        sizes[size],
        className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading && <Spinner />}
          {children}
        </>
      )}
    </Comp>
  );
});

function Spinner() {
  return (
    <span
      aria-hidden
      className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-r-transparent"
    />
  );
}
